import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeSEO from '@/components/SafeSEO';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, User, Tag, Loader2 } from 'lucide-react';
import { realtimeDb as db } from '@/lib/firebase';
import { ref, onValue } from 'firebase/database';
import { useLanguage } from '@/contexts/LanguageContext';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const newsRef = ref(db, 'news');

    const unsubscribe = onValue(
      newsRef,
      (snapshot) => {
        const data = snapshot.val();

        if (data) {
          const newsArray = Object.keys(data).map((key) => ({
            id: key,
            ...data[key]
          }));

          newsArray.sort((a, b) => new Date(b.date) - new Date(a.date));
          setNews(newsArray);
        } else {
          setNews([]);
        }

        setLoading(false);
      },
      (error) => {
        console.error('Error fetching news:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-[#0A5F7F]" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <SafeSEO
        title={t('news.title')}
        description={t('news.subtitle')}
      />

      <div className="bg-[#0A5F7F] text-white py-20">
        <div className="container mx-auto px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            {t('news.title')}
          </motion.h1>

          <p className="text-xl opacity-90 max-w-2xl">
            {t('news.heroSubtitle')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-16">
        {/* Featured App Launch Section (User Request) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 relative"
        >
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-8 md:p-12">
            <div className="text-white">
              <span className="inline-block px-4 py-1 rounded-full bg-white/20 backdrop-blur-md text-sm font-bold mb-6 uppercase tracking-widest">
                Lanzamiento Estratégico
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
                Hub CLAI HUB: La Oficina Digital Gremial
              </h2>
              <p className="text-xl opacity-90 mb-8 leading-relaxed">
                Transformamos la gestión de organizaciones empresariales con una plataforma 360° diseñada para la comunicación, el networking y la transparencia administrativa.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/capacitacion">
                  <button className="bg-white text-[#0A5F7F] hover:bg-gray-100 font-bold py-4 px-8 rounded-2xl transition-all shadow-lg flex items-center gap-2">
                    Conoce más del Hub
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/contacto">
                  <button className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold py-4 px-8 rounded-2xl transition-all">
                    Registrar mi Organización
                  </button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative">
              <div className="absolute inset-0 bg-white/20 blur-3xl rounded-full"></div>
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070&auto=format&fit=crop" 
                alt="Digital management dashboard for organizations" 
                className="relative z-10 rounded-3xl shadow-2xl border-4 border-white/10 transform rotate-2 hover:rotate-0 transition-transform duration-500"
              />
            </div>
          </div>
        </motion.div>

        {news.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">{t('news.noNews')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((item, index) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col"
              >
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-[#0A5F7F] text-white text-xs px-3 py-1 rounded-full font-medium uppercase tracking-wider flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {item.category || 'General'}
                    </span>
                  </div>

                  <img
                    src={item.image || 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {item.displayDate || item.date}
                    </div>

                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {item.author || 'Consulting Logistic'}
                    </div>
                  </div>

                  <h2 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2 hover:text-[#0A5F7F] transition-colors">
                    {item.externalLink ? (
                      <a
                        href={item.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.title}
                      </a>
                    ) : (
                      <Link to={`/noticias/${item.slug}`}>
                        {item.title}
                      </Link>
                    )}
                  </h2>

                  <p className="text-gray-600 mb-6 line-clamp-3 flex-grow">
                    {item.excerpt}
                  </p>

                  <div className="mt-auto pt-4 border-t border-gray-100">
                    {item.externalLink ? (
                      <a
                        href={item.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-[#0A5F7F] font-semibold hover:text-[#0D7FA3] transition-colors group"
                      >
                        {t('news.readSource')}
                        <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </a>
                    ) : (
                      <Link
                        to={`/noticias/${item.slug}`}
                        className="inline-flex items-center text-[#0A5F7F] font-semibold hover:text-[#0D7FA3] transition-colors group"
                      >
                        {t('news.readArticle')}
                        <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
